import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users } from "lucide-react";
import Logo from "@/components/aoria/Logo";

/**
 * Navigation principale — 8 routes-pages, plus aucune ancre.
 * Architecture multi-pages : chaque rubrique a sa propre URL.
 */
const links = [
    { to: "/", label: "Accueil", exact: true },
    { to: "/enjeu", label: "Enjeux" },
    { to: "/outils", label: "Outils" },
    { to: "/faq", label: "FAQ" },
    { to: "/manifeste", label: "Manifeste" },
    { to: "/ressources", label: "Ressources" },
    { to: "/a-propos", label: "À propos" },
    { to: "/contact", label: "Contact" },
];

export default function Navigation() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (l) => {
        if (l.exact) return location.pathname === l.to;
        // active on the page itself + on nested article pages (ressources/:slug)
        if (l.to === "/ressources") {
            return location.pathname.startsWith("/ressources");
        }
        return location.pathname === l.to;
    };

    const goParticiper = () => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/#participer");
            return;
        }
        const el = document.getElementById("participer");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="main-navigation"
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FDFDFC]/92 border-b border-[#E0E1DD] shadow-[0_1px_0_rgba(13,27,42,0.04)] transition-all duration-300"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                <div className="flex items-center justify-between h-20 md:h-[92px]">
                    <Link
                        to="/"
                        onClick={() => setOpen(false)}
                        data-testid="nav-logo"
                        className="transition-opacity hover:opacity-70 rounded outline-none focus-visible:outline-[#415A77]"
                        aria-label="Retour à l'accueil — Aoria"
                    >
                        <Logo
                            size={40}
                            tone="dark"
                            data-testid="nav-logo-mark"
                        />
                    </Link>

                    <nav
                        data-testid="nav-items-container"
                        aria-label="Navigation principale"
                        className="hidden xl:flex items-center gap-1 bg-[#FDFDFC]/90 backdrop-blur-md border border-[#0D1B2A]/15 rounded-full px-2 py-1.5 shadow-[0_2px_10px_rgba(13,27,42,0.06)]"
                    >
                        {links.map((l) => {
                            const active = isActive(l);
                            return (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-${l.to === "/" ? "accueil" : l.to.replace("/", "")}`}
                                    aria-current={active ? "page" : undefined}
                                    className={`relative text-[14.5px] font-medium tracking-[0.01em] px-4 py-2.5 rounded-full transition-all duration-300 outline-none focus-visible:outline-[#415A77] ${
                                        active
                                            ? "bg-[#0D1B2A] text-[#FDFDFC] shadow-[0_2px_8px_rgba(13,27,42,0.22)]"
                                            : "text-[#1B263B] hover:text-[#0D1B2A] hover:bg-[#0D1B2A]/[0.05]"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="hidden xl:block">
                        <button
                            data-testid="nav-cta-rejoindre"
                            onClick={goParticiper}
                            className="inline-flex items-center gap-2 rounded-full bg-[#0D1B2A] text-[#FDFDFC] text-sm font-medium pl-5 pr-6 py-3 tracking-wide shadow-[0_4px_14px_rgba(13,27,42,0.28)] hover:shadow-[0_6px_22px_rgba(13,27,42,0.42)] hover:bg-[#1B263B] transition-all"
                        >
                            <Users
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                            />
                            Rejoindre
                        </button>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="xl:hidden p-2 text-[#0D1B2A] rounded outline-none focus-visible:outline-[#415A77]"
                        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                        aria-expanded={open}
                        aria-controls="nav-mobile-menu"
                    >
                        <svg
                            width="26"
                            height="26"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                        >
                            {open ? (
                                <>
                                    <path d="M5 5 L19 19" />
                                    <path d="M19 5 L5 19" />
                                </>
                            ) : (
                                <>
                                    <path d="M3 7 H21" />
                                    <path d="M3 12 H21" />
                                    <path d="M3 17 H21" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    id="nav-mobile-menu"
                    data-testid="nav-mobile-menu"
                    className="xl:hidden bg-[#FDFDFC] border-t border-[#E0E1DD] max-h-[calc(100vh-92px)] overflow-y-auto"
                >
                    <nav
                        aria-label="Navigation principale — mobile"
                        className="px-6 py-7 flex flex-col gap-1.5"
                    >
                        {links.map((l) => {
                            const active = isActive(l);
                            return (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-${l.to === "/" ? "accueil" : l.to.replace("/", "")}`}
                                    aria-current={active ? "page" : undefined}
                                    className={`text-left text-base py-3 px-1 border-b border-[#E0E1DD] transition-colors duration-200 outline-none focus-visible:outline-[#415A77] ${
                                        active
                                            ? "text-[#0D1B2A] font-semibold"
                                            : "text-[#1B263B] hover:text-[#0D1B2A]"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            );
                        })}
                        <button
                            data-testid="nav-mobile-rejoindre"
                            onClick={goParticiper}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0D1B2A] text-[#FDFDFC] text-sm font-medium px-6 py-3.5 mt-5 w-fit shadow-[0_4px_14px_rgba(13,27,42,0.28)]"
                        >
                            <Users
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                            />
                            Rejoindre le projet
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}
